/**
 * The KPI dashboard's data for ONE scope — a property, or the portfolio rollup.
 *
 * Deliberately simpler than `useCabinetData`: KPI values change when an
 * extraction run finishes (minutes), not continuously, so this loads on scope
 * change and on demand rather than polling.
 *
 * A scope change (another property, another grain) refetches, but keeps the
 * previous grid mounted and marks it `stale` instead of blanking to a skeleton:
 * unmounting the grid on every grain click reads as a page reload. The previous
 * scope's numbers under the new scope's heading would still be a wrong answer,
 * not a stale one — so `stale` is the caller's contract to dim the grid and
 * stop it being read or clicked until the new answer lands, and a scope load
 * that FAILS drops the old table outright rather than leaving it on screen.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
	type KpiOverrideOutcome,
	type KpiRevertOutcome,
	kpiService,
} from "../../services/kpiService";
import type { KpiPeriodGrain, KpiTable } from "../../types/kpi";

export interface KpiScope {
	/** Null means the portfolio rollup. */
	propertyKey: string | null;
	/**
	 * Period bucket. Part of the SCOPE, not a display option: the server does
	 * the aggregation, so changing it changes which numbers exist — a quarterly
	 * grid is not a monthly one relabelled.
	 */
	grain?: KpiPeriodGrain;
	from?: string;
	to?: string;
	/**
	 * The workflow whose dashboard this is (public id). Part of the SCOPE for
	 * the same reason `grain` is: it changes which numbers exist, so a change
	 * refetches rather than filtering what is already on screen.
	 */
	workflowId?: string;
}

export interface KpiTableData {
	table: KpiTable | null;
	loading: boolean;
	error: Error | undefined;
	/**
	 * The server said this account has no KPI scope at all (403). Not an
	 * error: there is nothing to load, so the dashboard shows an empty state
	 * rather than a red banner over a Retry that can only 403 again.
	 */
	unavailable: boolean;
	refreshing: boolean;
	/**
	 * The table on screen belongs to the PREVIOUS scope — a scope change is in
	 * flight. Kept mounted so the grid does not flash away under the user, but
	 * the caller must show it as not-yet-the-answer (dimmed, not interactive)
	 * and must not let it be exported: it is another scope's numbers.
	 */
	stale: boolean;
	refresh: () => void;
	/**
	 * Save a manual override, then reload so every dependent number (derived
	 * metrics, the rollup) comes back from the server rather than being patched
	 * client-side — the epic keeps all math server-side.
	 *
	 * Resolves the outcome rather than a boolean, so the panel can distinguish
	 * "superseded by a newer extraction" from "the save failed". A conflict ALSO
	 * reloads — the point of the message is that there is a newer value to look
	 * at, so it had better be on screen — but it never re-attempts the save.
	 */
	override: (valueId: string, value: number) => Promise<KpiOverrideOutcome>;
	/**
	 * Undo a manual override, then reload — the same discipline as `override`:
	 * a success moves dependent numbers (derived metrics, the rollup) and a
	 * conflict means the row the user was looking at changed, so the newer
	 * state has to be on screen. A plain failure reloads nothing — nothing
	 * changed, and a refetch would only hide the error behind a spinner.
	 */
	revert: (valueId: string) => Promise<KpiRevertOutcome>;
}

/**
 * `initial` has nothing to show yet (skeleton); `scope` has the previous
 * scope's grid to hold on screen while the new one loads; `refresh` is a
 * refetch of the SAME scope, where what is on screen is still the right answer.
 */
type KpiLoadMode = "initial" | "scope" | "refresh";

export function useKpiTable(scope: KpiScope): KpiTableData {
	const { propertyKey, grain, from, to, workflowId } = scope;
	const [table, setTable] = useState<KpiTable | null>(null);
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [stale, setStale] = useState(false);
	const [error, setError] = useState<Error | undefined>(undefined);
	const [unavailable, setUnavailable] = useState(false);

	const mounted = useRef(true);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);

	// Rising counter, so a stale in-flight response from the previous scope
	// cannot overwrite the current one when the user switches tabs quickly.
	const requestId = useRef(0);

	const load = useCallback(
		async (mode: KpiLoadMode) => {
			const id = ++requestId.current;
			if (mode === "initial") setLoading(true);
			else setRefreshing(true);
			if (mode === "scope") setStale(true);
			// A new scope answers a different question, so the previous scope's
			// error does not describe it: clearing it here is what lets the
			// skeleton (or the held grid) show instead of a stale red banner.
			if (mode !== "refresh") setError(undefined);
			const next = await kpiService.getTable({
				propertyKey: propertyKey ?? undefined,
				rollup: propertyKey === null,
				grain,
				from,
				to,
				workflowId,
			});
			if (!mounted.current || id !== requestId.current) return;
			if (next.kind === "failed") {
				// A scope change that failed must not leave the previous scope's
				// numbers under the new scope's heading — that is a wrong answer,
				// not a stale one. Drop them and let the error state stand.
				if (mode === "scope") setTable(null);
				setError(new Error("Could not load the KPI table."));
			} else if (next.kind === "unavailable") {
				// No scope to read. Clear any previous table with it: keeping the
				// last one on screen would present another scope's numbers as
				// this account's.
				setTable(null);
				setUnavailable(true);
				setError(undefined);
			} else {
				setTable(next.table);
				setUnavailable(false);
				setError(undefined);
			}
			setLoading(false);
			setRefreshing(false);
			setStale(false);
		},
		[propertyKey, grain, from, to, workflowId],
	);

	// What is on screen, readable from the effect below without making the
	// effect depend on it — a table arriving must not re-run the load.
	const tableRef = useRef<KpiTable | null>(null);
	useEffect(() => {
		tableRef.current = table;
	});

	// The `load` this effect last started. StrictMode's dev remount re-runs the
	// effect with the SAME `load`, and without the guard every mount pays a
	// duplicate table fetch; a scope change makes a NEW `load`, so it refetches.
	// The skipped run's data is not lost — the first run's response applies
	// after the remount (`mounted` is re-armed and `requestId` unchanged).
	const lastAutoLoadRef = useRef<typeof load | null>(null);
	useEffect(() => {
		if (lastAutoLoadRef.current === load) return;
		// Only a scope change that has a grid to hold gets to hold one; the
		// first load, or one after an empty/failed scope, has nothing but the
		// skeleton to show.
		const holdsPreviousGrid =
			lastAutoLoadRef.current !== null && tableRef.current !== null;
		lastAutoLoadRef.current = load;
		void load(holdsPreviousGrid ? "scope" : "initial");
	}, [load]);

	const override = useCallback(
		async (valueId: string, value: number): Promise<KpiOverrideOutcome> => {
			const outcome = await kpiService.overrideValue(valueId, value);
			// Reload on success (dependent numbers moved) and on conflict (the row
			// the user is looking at is stale). Never on a plain failure: nothing
			// changed, and a refetch would only hide the error behind a spinner.
			// Neither branch retries the save — a superseded row rejects it every
			// time, so a retry loop would spin forever against a permanent answer.
			if (outcome.kind !== "failed") await load("refresh");
			return outcome;
		},
		[load],
	);

	const revert = useCallback(
		async (valueId: string): Promise<KpiRevertOutcome> => {
			const outcome = await kpiService.revertValue(valueId);
			// Same reload discipline as `override`: success (the extracted number
			// is back, and everything computed from it has to move too) and
			// conflict (the row is stale; its current state has to be on screen)
			// both refetch; a plain failure changes nothing.
			if (outcome.kind !== "failed") await load("refresh");
			return outcome;
		},
		[load],
	);

	return useMemo(
		() => ({
			table,
			loading,
			error,
			unavailable,
			refreshing,
			stale,
			refresh: () => void load("refresh"),
			override,
			revert,
		}),
		[
			table,
			loading,
			error,
			unavailable,
			refreshing,
			stale,
			load,
			override,
			revert,
		],
	);
}
